import React from "react";
import PropTypes from "prop-types";
import {
  NavLink as RouterLink,
  useLocation,
} from "react-router-dom";

import {
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
} from "@material-ui/core";

/* =====================================================
   ICONS
===================================================== */

// Main
import DashboardIcon from "@material-ui/icons/Dashboard";

// Operations
import EventIcon from "@material-ui/icons/Event";
import AddBoxIcon from "@material-ui/icons/AddBox";
import ListAltIcon from "@material-ui/icons/ListAlt";
import SearchIcon from "@material-ui/icons/Search";
import ReceiptIcon from "@material-ui/icons/Receipt";
import CreateNewFolderIcon from "@material-ui/icons/CreateNewFolder";
import ReorderIcon from "@material-ui/icons/Reorder";
import SettingsApplicationsIcon from "@material-ui/icons/SettingsApplications";
import QueuePlayNextIcon from "@material-ui/icons/QueuePlayNext";
import FlagIcon from "@material-ui/icons/Flag";

// Administration
import SupervisedUserCircleIcon from "@material-ui/icons/SupervisedUserCircle";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import GroupAddIcon from "@material-ui/icons/GroupAdd";

// Settings
import AnnouncementIcon from "@material-ui/icons/Announcement";

// Reports / Documents
import ReportIcon from "@material-ui/icons/Report";

// Misc
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";

/* =====================================================
   STYLES
===================================================== */

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1, 0),
  },

  item: {
    display: "flex",
    padding: 0,
  },

  button: {
    width: "100%",
    minHeight: 44,
    padding: theme.spacing(1, 2),
    borderRadius: 7,
    color: "#4a4a4a",
    justifyContent: "flex-start",
    textTransform: "none",

    "&:hover": {
      backgroundColor: "#f3f4f6",
    },

    "& .MuiListItemIcon-root": {
      minWidth: 40,
      color: "#6b7280",
    },

    "& .MuiListItemText-primary": {
      fontSize: 14,
      fontWeight: 500,
    },
  },

  active: {
    backgroundColor: "#eaf7f1 !important",
    color: "#088c3f",

    "& .MuiListItemIcon-root": {
      color: "#088c3f",
    },

    "& .MuiListItemText-primary": {
      fontWeight: 600,
    },
  },

  parent: {
    marginTop: 2,
  },

  nested: {
    minHeight: 40,
    padding: theme.spacing(
      0.75,
      2,
      0.75,
      6
    ),
    borderRadius: 7,
    color: "#666",

    "&:hover": {
      backgroundColor: "#f5f6f7",
    },

    "& .MuiListItemIcon-root": {
      minWidth: 34,
      color: "#8a8a8a",
    },

    "& .MuiListItemText-primary": {
      fontSize: 13,
      fontWeight: 500,
    },
  },

  nestedActive: {
    backgroundColor: "#f0f8f4 !important",
    color: "#088c3f",

    "& .MuiListItemIcon-root": {
      color: "#088c3f",
    },

    "& .MuiListItemText-primary": {
      fontWeight: 600,
    },
  },

  sectionTitle: {
    padding: theme.spacing(
      2,
      2,
      0.75
    ),
    color: "#9a9a9a",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },

  divider: {
    margin: theme.spacing(1, 2),
  },
}));

/* =====================================================
   COMPONENT
===================================================== */

const NavItem = ({
  className,
  ...rest
}) => {
  const classes = useStyles();
  const location = useLocation();

  /* ===================================================
     MENU STATES
  =================================================== */

  const [
    openAuctions,
    setOpenAuctions,
  ] = React.useState(false);

  const [
    openPrivateSale,
    setOpenPrivateSale,
  ] = React.useState(false);

  const [
    openExhibitions,
    setOpenExhibitions,
  ] = React.useState(false);

  const [
    openInventory,
    setOpenInventory,
  ] = React.useState(false);

  const [
    openAccounting,
    setOpenAccounting,
  ] = React.useState(false);

  const [
    openReports,
    setOpenReports,
  ] = React.useState(false);

  const [
    openDocuments,
    setOpenDocuments,
  ] = React.useState(false);

  const [
    openUsers,
    setOpenUsers,
  ] = React.useState(false);

  const [
    openSettings,
    setOpenSettings,
  ] = React.useState(false);

  /* ===================================================
     CURRENT ROUTE
  =================================================== */

  const pathname = location.pathname;

  /* ===================================================
     AUTO OPEN MENU
  =================================================== */

  React.useEffect(() => {
    setOpenAuctions(
      pathname.startsWith("/app/auctions") ||
      pathname.startsWith("/app/find/lot") ||
      pathname.startsWith("/app/update/lot")
    );

    setOpenPrivateSale(
      pathname.startsWith("/app/private")
    );

    setOpenExhibitions(
      pathname.startsWith(
        "/app/exihibitions"
      )
    );

    setOpenInventory(
      pathname.startsWith(
        "/app/invetory"
      )
    );

    setOpenAccounting(
      pathname === "/app/invoice" ||
      pathname.startsWith(
        "/app/find/invoice"
      ) ||
      pathname.startsWith(
        "/app/report/account"
      )
    );

    setOpenReports(
      pathname.startsWith(
        "/app/report/releasing"
      ) ||
      pathname.startsWith(
        "/app/report/consignment"
      ) ||
      pathname.startsWith(
        "/app/report/commission"
      ) ||
      pathname.startsWith(
        "/app/report/private"
      )
    );

    setOpenDocuments(
      pathname.startsWith(
        "/app/receipt/certificate"
      ) ||
      pathname.startsWith(
        "/app/report/sticker"
      ) ||
      pathname.startsWith(
        "/app/report/custom"
      ) ||
      pathname.startsWith(
        "/app/report/certificate"
      )
    );

    setOpenUsers(
      pathname.startsWith(
        "/app/user"
      )
    );

    setOpenSettings(
      pathname.startsWith(
        "/app/setting"
      )
    );
  }, [pathname]);

  /* ===================================================
     TOGGLE
  =================================================== */

  const toggle = (
    setter,
    value
  ) => {
    setter(!value);
  };

  /* ===================================================
     LOGOUT
  =================================================== */

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/";
  };

  /* ===================================================
     RENDER
  =================================================== */

  return (
    <List
      component="nav"
      className={`${classes.root} ${
        className || ""
      }`}
      {...rest}
    >

      {/* =================================================
          MAIN
      ================================================= */}

      <ListItem
        button
        component={RouterLink}
        to="/app/dashboard"
        activeClassName={
          classes.active
        }
        className={classes.button}
      >
        <ListItemIcon>
          <DashboardIcon />
        </ListItemIcon>

        <ListItemText
          primary="Dashboard"
        />
      </ListItem>


      {/* =================================================
          OPERATIONS
      ================================================= */}

      <div className={classes.sectionTitle}>
        Operations
      </div>


      {/* =================================================
          AUCTIONS MANAGEMENT
      ================================================= */}

      <ListItem
        button
        onClick={() =>
          toggle(
            setOpenAuctions,
            openAuctions
          )
        }
        className={`${classes.button} ${classes.parent}`}
      >
        <ListItemIcon>
          <EventIcon />
        </ListItemIcon>

        <ListItemText
          primary="Auctions Management"
        />

        {openAuctions ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>

      <Collapse
        in={openAuctions}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >

          <ListItem
            button
            component={RouterLink}
            to="/app/auctions/create/room"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <AddBoxIcon />
            </ListItemIcon>

            <ListItemText
              primary="Create Auction"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/auctions/list"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>

            <ListItemText
              primary="Auction List"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/find/lot"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <SearchIcon />
            </ListItemIcon>

            <ListItemText
              primary="Find Lots"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/update/lot/hpandBp"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <SettingsApplicationsIcon />
            </ListItemIcon>

            <ListItemText
              primary="Update Hammer Price"
            />
          </ListItem>

        </List>
      </Collapse>


      {/* =================================================
          PRIVATE SALES
      ================================================= */}

      <ListItem
        button
        onClick={() =>
          toggle(
            setOpenPrivateSale,
            openPrivateSale
          )
        }
        className={`${classes.button} ${classes.parent}`}
      >
        <ListItemIcon>
          <ReceiptIcon />
        </ListItemIcon>

        <ListItemText
          primary="Private Sales"
        />

        {openPrivateSale ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>

      <Collapse
        in={openPrivateSale}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >

          <ListItem
            button
            component={RouterLink}
            to="/app/private/create"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <CreateNewFolderIcon />
            </ListItemIcon>

            <ListItemText
              primary="Create Private Sale"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/private/list"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ReorderIcon />
            </ListItemIcon>

            <ListItemText
              primary="Private Sale List"
            />
          </ListItem>

        </List>
      </Collapse>


      {/* =================================================
          EXHIBITIONS
      ================================================= */}

      <ListItem
        button
        onClick={() =>
          toggle(
            setOpenExhibitions,
            openExhibitions
          )
        }
        className={`${classes.button} ${classes.parent}`}
      >
        <ListItemIcon>
          <ListAltIcon />
        </ListItemIcon>

        <ListItemText
          primary="Exhibitions"
        />

        {openExhibitions ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>

      <Collapse
        in={openExhibitions}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >

          <ListItem
            button
            component={RouterLink}
            to="/app/exihibitions/create"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <AddBoxIcon />
            </ListItemIcon>

            <ListItemText
              primary="Create Exhibition"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/exihibitions/list"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>

            <ListItemText
              primary="Exhibition List"
            />
          </ListItem>

        </List>
      </Collapse>


      {/* =================================================
          INVENTORY MANAGEMENT
      ================================================= */}

      <ListItem
        button
        onClick={() =>
          toggle(
            setOpenInventory,
            openInventory
          )
        }
        className={`${classes.button} ${classes.parent}`}
      >
        <ListItemIcon>
          <QueuePlayNextIcon />
        </ListItemIcon>

        <ListItemText
          primary="Inventory Management"
        />

        {openInventory ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>

      <Collapse
        in={openInventory}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >

          <ListItem
            button
            component={RouterLink}
            to="/app/invetory/create"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <AddBoxIcon />
            </ListItemIcon>

            <ListItemText
              primary="Create Item"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/invetory/list/item"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>

            <ListItemText
              primary="Items"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/invetory/receive/item"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <QueuePlayNextIcon />
            </ListItemIcon>

            <ListItemText
              primary="Receive Items"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/invetory/release/item"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <FlagIcon />
            </ListItemIcon>

            <ListItemText
              primary="Release Items"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/invetory/transit/item"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <QueuePlayNextIcon />
            </ListItemIcon>

            <ListItemText
              primary="Items in Transit"
            />
          </ListItem>

        </List>
      </Collapse>


      {/* =================================================
          ACCOUNTING
      ================================================= */}

      <div className={classes.sectionTitle}>
        Accounting
      </div>

      <ListItem
        button
        onClick={() =>
          toggle(
            setOpenAccounting,
            openAccounting
          )
        }
        className={`${classes.button} ${classes.parent}`}
      >
        <ListItemIcon>
          <ReceiptIcon />
        </ListItemIcon>

        <ListItemText
          primary="Accounting"
        />

        {openAccounting ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>

      <Collapse
        in={openAccounting}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >

          {/* INVOICE */}

          

          {/* BULK INVOICE */}

          <ListItem
            button
            component={RouterLink}
            to="/app/invoice"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>

            <ListItemText
              primary="Invoice"
            />
          </ListItem>

          {/* ACCOUNTS RECEIVABLE */}

          <ListItem
            button
            component={RouterLink}
            to="/app/report/account/acknowledge/receipt"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <SupervisorAccountIcon />
            </ListItemIcon>

            <ListItemText
              primary="Accounts Receivable"
            />
          </ListItem>

        </List>
      </Collapse>


      {/* =================================================
          REPORTS & RECORDS
      ================================================= */}

      <div className={classes.sectionTitle}>
        Reports & Records
      </div>

      <ListItem
        button
        onClick={() =>
          toggle(
            setOpenReports,
            openReports
          )
        }
        className={`${classes.button} ${classes.parent}`}
      >
        <ListItemIcon>
          <ReportIcon />
        </ListItemIcon>

        <ListItemText
          primary="Reports & Records"
        />

        {openReports ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>

      <Collapse
        in={openReports}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >

          {/* RELEASING */}

          <ListItem
            button
            component={RouterLink}
            to="/app/report/releasing"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <FlagIcon />
            </ListItemIcon>

            <ListItemText
              primary="Releasing"
            />
          </ListItem>

          {/* CONSIGNMENT */}

          <ListItem
            button
            component={RouterLink}
            to="/app/report/consignment"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>

            <ListItemText
              primary="Consignment"
            />
          </ListItem>

          {/* COMMISSION */}

          <ListItem
            button
            component={RouterLink}
            to="/app/report/commission"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ReceiptIcon />
            </ListItemIcon>

            <ListItemText
              primary="Commission"
            />
          </ListItem>

          {/* PRIVATE SALE */}

          <ListItem
            button
            component={RouterLink}
            to="/app/report/private"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ReceiptIcon />
            </ListItemIcon>

            <ListItemText
              primary="Private Sale"
            />
          </ListItem>

        </List>
      </Collapse>


      {/* =================================================
          DOCUMENT GENERATION
      ================================================= */}

      <div className={classes.sectionTitle}>
        Document Generation
      </div>

      <ListItem
        button
        onClick={() =>
          toggle(
            setOpenDocuments,
            openDocuments
          )
        }
        className={`${classes.button} ${classes.parent}`}
      >
        <ListItemIcon>
          <ListAltIcon />
        </ListItemIcon>

        <ListItemText
          primary="Document Generation"
        />

        {openDocuments ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>

      <Collapse
        in={openDocuments}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >

          {/* RECEIPT & CERTIFICATE */}

          <ListItem
            button
            component={RouterLink}
            to="/app/receipt/certificate/invoice/create"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ReceiptIcon />
            </ListItemIcon>

            <ListItemText
              primary="Receipt & Certificate"
            />
          </ListItem>

          {/* STICKER GENERATION */}

          <ListItem
            button
            component={RouterLink}
            to="/app/report/sticker"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>

            <ListItemText
              primary="Sticker Generation"
            />
          </ListItem>

          {/* CUSTOM PRINT */}

          <ListItem
            button
            component={RouterLink}
            to="/app/report/custom"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <SettingsApplicationsIcon />
            </ListItemIcon>

            <ListItemText
              primary="Custom Print"
            />
          </ListItem>

          {/* CERTIFICATE GENERATION */}

          <ListItem
            button
            component={RouterLink}
            to="/app/report/certificate"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ReceiptIcon  />
            </ListItemIcon>

            <ListItemText
              primary="Certificate Generation"
            />
          </ListItem>
                    
          {/* =================================================
              CUSTOMER LIST
          ================================================= */}

          <ListItem
            button
            component={RouterLink}
            to="/app/customer/list"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <SupervisedUserCircleIcon />
            </ListItemIcon>

            <ListItemText
              primary="Customer List"
            />
          </ListItem>   
        </List>
      </Collapse>


      {/* =================================================
          ADMINISTRATION
      ================================================= */}

      <div className={classes.sectionTitle}>
        Administration
      </div>

      <ListItem
        button
        onClick={() =>
          toggle(
            setOpenUsers,
            openUsers
          )
        }
        className={`${classes.button} ${classes.parent}`}
      >
        <ListItemIcon>
          <SupervisorAccountIcon />
        </ListItemIcon>

        <ListItemText
          primary="User Management"
        />

        {openUsers ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>

      <Collapse
        in={openUsers}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >

          <ListItem
            button
            component={RouterLink}
            to="/app/user/create"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <GroupAddIcon />
            </ListItemIcon>

            <ListItemText
              primary="Create User"
            />
          </ListItem>

          <ListItem
            button
            component={RouterLink}
            to="/app/user/list"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <SupervisedUserCircleIcon />
            </ListItemIcon>

            <ListItemText
              primary="User List"
            />
          </ListItem>

        </List>
      </Collapse>


      {/* =================================================
          SETTINGS
      ================================================= */}

      <ListItem
        button
        onClick={() =>
          toggle(
            setOpenSettings,
            openSettings
          )
        }
        className={`${classes.button} ${classes.parent}`}
      >
        <ListItemIcon>
          <SettingsApplicationsIcon />
        </ListItemIcon>

        <ListItemText
          primary="Settings"
        />

        {openSettings ? (
          <ExpandLess />
        ) : (
          <ExpandMore />
        )}
      </ListItem>

      <Collapse
        in={openSettings}
        timeout="auto"
        unmountOnExit
      >
        <List
          component="div"
          disablePadding
        >

          {/* HERO BANNER */}

          <ListItem
            button
            component={RouterLink}
            to="/app/setting/banner/list"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>

            <ListItemText
              primary="Hero Banner"
            />
          </ListItem>

          {/* INVOICE MESSAGE SETTINGS */}

          <ListItem
            button
            component={RouterLink}
            to="/app/setting/message"
            activeClassName={
              classes.nestedActive
            }
            className={classes.nested}
          >
            <ListItemIcon>
              <AnnouncementIcon />
            </ListItemIcon>

            <ListItemText
              primary="Invoice Message Settings"
            />
          </ListItem>

        </List>
      </Collapse>


      {/* =================================================
          DIVIDER
      ================================================= */}

      <Divider
        className={classes.divider}
      />


      {/* =================================================
          LOGOUT
      ================================================= */}

      <ListItem
        button
        onClick={handleLogout}
        className={classes.button}
      >
        <ListItemIcon>
          <ExitToAppIcon />
        </ListItemIcon>

        <ListItemText
          primary="Logout"
        />
      </ListItem>

    </List>
  );
};

NavItem.propTypes = {
  className: PropTypes.string,
};

export default NavItem;